import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react'
import { activeElementWithin, focusableWithin } from '../../runtime/focus'
import { Portal } from '../../runtime/portal'

interface DialogProps {
  open: boolean
  onClose(): void
  title: string
  children: ReactNode
  testId?: string
}

/**
 * A modal dialog, for the few moments that must interrupt: it takes focus
 * when it opens, keeps Tab inside itself, closes on Escape or a press on the
 * backdrop, and hands focus back to the control that opened it.
 *
 * It renders through the mount's portal host, so it stays inside the shadow
 * root where the styles apply, and is `position: fixed` to clear whatever
 * Webflow container is clipping the mount — the arrangement the account menu
 * uses, with the same limit: an ancestor with a transform would contain it.
 */
export function Dialog({ open, onClose, title, children, testId }: DialogProps) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()

  // Focus lands on the panel, so the title is read first; it goes back to the
  // opener when the dialog closes, however it closes.
  useEffect(() => {
    if (!open) return
    const opener = activeElementWithin(panel.current)
    panel.current?.focus()
    return () => {
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [open])

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    if (event.key !== 'Tab' || !panel.current) return

    // Tab cycles within the dialog. From the panel itself, where focus lands
    // on open, it goes to the first control, or the last going backwards.
    const controls = focusableWithin(panel.current)
    const first = controls[0]
    const last = controls[controls.length - 1]
    if (!first || !last) {
      event.preventDefault()
      return
    }
    const active = activeElementWithin(panel.current)
    const atEdge = event.shiftKey ? active === first || active === panel.current : active === last
    if (atEdge) {
      event.preventDefault()
      ;(event.shiftKey ? last : first).focus()
    }
  }

  function onBackdropPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) onClose()
  }

  if (!open) return null

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
        onPointerDown={onBackdropPointerDown}
        data-testid={testId && `${testId}-backdrop`}
      >
        <div
          ref={panel}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className="w-full max-w-md rounded-card bg-white p-6 shadow-xl outline-none"
          data-testid={testId}
        >
          <h2 id={titleId} className="text-xl font-bold text-ink">
            {title}
          </h2>
          {children}
        </div>
      </div>
    </Portal>
  )
}
